import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { useState } from 'react';

export function CookiePreferences() {
  const { showPreferences, setShowPreferences, preferences, updateConsent } = useCookieConsent();
  const [localPrefs, setLocalPrefs] = useState(preferences);

  const handleSave = () => {
    updateConsent(localPrefs);
  };

  return (
    <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences. You can enable or disable different types of cookies below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary Cookies */}
          <div className="flex items-start justify-between space-x-4 pb-4 border-b">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Label htmlFor="necessary" className="text-base font-semibold">
                  Necessary Cookies
                </Label>
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">Always Active</span>
              </div>
              <p className="text-sm text-muted-foreground">
                These cookies are essential for the website to function properly. They enable basic features like page navigation, 
                access to secure areas, and remember your preferences. The website cannot function properly without these cookies.
              </p>
            </div>
            <Switch
              id="necessary"
              checked={true}
              disabled
              className="mt-1"
            />
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start justify-between space-x-4 pb-4 border-b">
            <div className="flex-1">
              <Label htmlFor="analytics" className="text-base font-semibold mb-2 block">
                Analytics Cookies
              </Label>
              <p className="text-sm text-muted-foreground">
                These cookies help us understand how visitors interact with our website by collecting and reporting information 
                anonymously. This includes page views, visit duration, scroll depth, and traffic sources. No personal data is collected.
              </p>
            </div>
            <Switch
              id="analytics"
              checked={localPrefs.analytics}
              onCheckedChange={(checked) => setLocalPrefs({ ...localPrefs, analytics: checked })}
              className="mt-1"
            />
          </div>

          {/* Advertising Cookies */}
          <div className="flex items-start justify-between space-x-4 pb-4 border-b">
            <div className="flex-1">
              <Label htmlFor="advertising" className="text-base font-semibold mb-2 block">
                Personalized Advertising
              </Label>
              <p className="text-sm text-muted-foreground">
                Enable personalized ads tailored to your interests. If disabled, you'll see contextual, 
                non-personalized ads based only on page content (GDPR-compliant without consent).
              </p>
            </div>
            <Switch
              id="advertising"
              checked={localPrefs.advertising}
              onCheckedChange={(checked) => setLocalPrefs({ ...localPrefs, advertising: checked })}
              className="mt-1"
            />
          </div>

          {/* Affiliate Cookies */}
          <div className="flex items-start justify-between space-x-4">
            <div className="flex-1">
              <Label htmlFor="affiliate" className="text-base font-semibold mb-2 block">
                Affiliate Marketing Cookies
              </Label>
              <p className="text-sm text-muted-foreground">
                These cookies are used to track affiliate referrals and commissions when you click on product links or make purchases 
                through our affiliate partners (TravelPayouts). They help us earn a small commission at no extra cost to you.
              </p>
            </div>
            <Switch
              id="affiliate"
              checked={localPrefs.affiliate}
              onCheckedChange={(checked) => setLocalPrefs({ ...localPrefs, affiliate: checked })}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowPreferences(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
